/**
 * MDL.make 延長保証 動的価格表示スクリプト
 * 
 * 概要:
 *   商品ページでオプション変更時に、保証料金をリアルタイムで動的算出・表示する。
 *   Cart Transform と同じロジック（合計 × 料率 → 百の位切り上げ）で計算し、
 *   Globo Product Options の addOn 表示を上書きする。
 * 
 * 対象: 新保証商品（gpo-data-product-handle に "保証延長" を含むチェックボックス）のみ。
 *        旧保証商品には干渉しない。
 * 
 * 設置場所: sections/main-product.liquid の末尾付近に <script src="..."> で読み込み
 */
(function () {
  'use strict';

  // ============================================================
  // 設定（Cart Transform 側と必ず合わせること）
  // ============================================================
  const CONFIG = {
    // 保証商品の Globo ハンドル（部分一致で判定）
    warrantyHandleKeyword: 'warranty',

    // 料率（Cart Transform のデフォルトと同一）
    rates: {
      '2year': 0.06,
      '3year': 0.12
    },

    // 端数処理: 百の位切り上げ
    roundUp: 100,

    // セレクタ
    selectors: {
      // Globo が更新する価格要素（data-amount 属性を持つ）
      priceAmount: 'span.price--final[data-amount]',
      // Globo が更新する価格テキスト（data-gpo-product-unit-price 属性）
      priceText: '[data-gpo-product-unit-price]',
      // addOn 価格表示
      addOnSpan: 'span.addOn',
      // チェックボックスラベル内の全体テキスト
      choiceValue: 'span.choicelist-item__value',
    },

    // デバッグモード（本番では false に）
    debug: false
  };

  // ============================================================
  // ユーティリティ
  // ============================================================
  function log(...args) {
    if (CONFIG.debug) console.log('[MDL-Warranty]', ...args);
  }

  /** 百の位切り上げ */
  function ceilToUnit(n, unit) {
    return Math.ceil(n / unit) * unit;
  }

  /** 数値を ¥XX,XXX 形式にフォーマット */
  function formatYen(n) {
    return '¥' + n.toLocaleString('ja-JP');
  }

  /** data-amount から数値を取得（"301,800" → 301800） */
  function parseAmount(str) {
    if (!str) return 0;
    return parseInt(str.replace(/,/g, ''), 10) || 0;
  }

  // ============================================================
  // メインロジック
  // ============================================================

  /**
   * Globo が最後に設定した保証抜きの商品合計を保持する変数
   */
  var _baseTotal = 0;

  /**
   * 価格テキストから数値を読み取る
   */
  function readPriceFromDOM() {
    var el = document.querySelector(CONFIG.selectors.priceAmount);
    if (!el) return 0;
    var text = el.textContent.replace(/[^0-9]/g, '');
    return parseInt(text, 10) || 0;
  }

  /**
   * Globo が価格を更新した時に呼ぶ。ベース金額を記録する。
   * （Globo の価格は保証¥0なので常に保証抜きの合計）
   */
  function updateBaseTotal() {
    _baseTotal = readPriceFromDOM();
    log('ベース金額更新:', _baseTotal);
  }

  /**
   * 現在の保証抜き合計を返す
   */
  function getProductTotal() {
    return _baseTotal;
  }

  /**
   * 保証チェックボックスを全て取得（新保証商品のみ）
   * gpo-data-product-handle に "保証延長" を含むものだけ対象。
   */
  function getWarrantyCheckboxes() {
    const all = document.querySelectorAll('input[type="checkbox"][gpo-data-product-handle]');
    const result = [];
    all.forEach(function (cb) {
      const handle = cb.getAttribute('gpo-data-product-handle') || '';
      if (handle.indexOf(CONFIG.warrantyHandleKeyword) !== -1) {
        result.push(cb);
      }
    });
    log('保証チェックボックス検出数:', result.length);
    return result;
  }

  /**
   * チェックボックスの value テキストから保証年数を判定して料率を返す。
   * 「メーカー標準1年保証(無料)」など加算対象外の選択肢は 0 を返す。
   */
  function getRateForCheckbox(cb) {
    const value = cb.getAttribute('value') || '';
    if (value.indexOf('3年') !== -1) {
      return CONFIG.rates['3year'];
    }
    if (value.indexOf('2年') !== -1) {
      return CONFIG.rates['2year'];
    }
    return 0;
  }

  /**
   * 保証料を算出
   */
  function calcWarrantyPrice(total, rate) {
    return ceilToUnit(total * rate, CONFIG.roundUp);
  }

  /**
   * 保証チェックボックス周辺の表示を整える。
   * 動的計算は維持するが、保証横の (+ ¥X,XXX) 表示は出さない方針。
   * 既存 addOn / Globo デフォルトの $0 表示は残骸防止のためクリアする。
   */
  function updateWarrantyDisplays() {
    const total = getProductTotal();
    if (total <= 0) return;

    const checkboxes = getWarrantyCheckboxes();

    checkboxes.forEach(function (cb) {
      const label = document.querySelector('label[for="' + cb.id + '"]');
      if (!label) {
        log('ラベルが見つかりません:', cb.id);
        return;
      }

      var addOnSpan = label.querySelector(CONFIG.selectors.addOnSpan);
      if (addOnSpan) {
        addOnSpan.textContent = '';
      }

      var choiceSpan = label.querySelector(CONFIG.selectors.choiceValue);
      if (choiceSpan) {
        var textNodes = [];
        choiceSpan.childNodes.forEach(function (node) {
          if (node.nodeType === 3 && node.textContent.indexOf('$0') !== -1) {
            textNodes.push(node);
          }
        });
        textNodes.forEach(function (node) { node.textContent = ''; });
      }
    });

    // メイン価格表示の更新（チェック済みの保証料を合計に加算）
    updateMainPriceDisplay(total, checkboxes);
  }

  /**
   * メインの価格表示を更新
   * 保証チェックボックスが ON の場合、保証料を加算した金額を表示。
   * data-amount は触らず、表示テキストだけ上書きする。
   */
  function updateMainPriceDisplay(total, checkboxes) {
    let warrantyTotal = 0;

    checkboxes.forEach(function (cb) {
      if (cb.checked) {
        const rate = getRateForCheckbox(cb);
        warrantyTotal += calcWarrantyPrice(total, rate);
      }
    });

    const priceEl = document.querySelector(CONFIG.selectors.priceAmount);
    if (!priceEl) return;

    const displayTotal = total + warrantyTotal;
    // テキストのみ上書き（フラグで自身のObserver発火を防止）
    _updatingPrice = true;
    priceEl.textContent = formatYen(displayTotal);
    // 少し遅延してフラグを戻す（Observerがmicrotaskで発火するため）
    setTimeout(function() { _updatingPrice = false; }, 50);
    log('メイン価格表示更新:', formatYen(total), '+', formatYen(warrantyTotal), '=', formatYen(displayTotal));
  }

  // ============================================================
  // 監視・イベント設定
  // ============================================================

  /**
   * MutationObserver: Globo が価格テキストを更新したら再計算
   * 自身の更新による無限ループを防ぐためフラグで制御
   */
  var _updatingPrice = false;

  function observePriceChanges() {
    var priceEl = document.querySelector(CONFIG.selectors.priceAmount);
    if (!priceEl) {
      log('価格要素が見つからないため、監視を開始できません');
      return;
    }

    var observer = new MutationObserver(function (mutations) {
      if (_updatingPrice) return; // 自身の更新は無視
      log('価格テキスト変更検出');
      updateBaseTotal();
      updateWarrantyDisplays();
    });

    observer.observe(priceEl, {
      childList: true,
      characterData: true,
      subtree: true
    });

    log('価格変更の監視を開始しました');
  }

  /**
   * 保証チェックボックスの change イベントを監視
   * チェック ON/OFF でメイン価格表示を更新するため。
   */
  function observeCheckboxChanges() {
    const checkboxes = getWarrantyCheckboxes();
    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', function () {
        log('保証チェックボックス変更:', cb.id, '→', cb.checked);
        updateWarrantyDisplays();
      });
    });
  }

  /**
   * Globo の DOM が遅延ロードされる場合に備えた初期化
   */
  function init() {
    log('初期化開始');

    // Globo の要素が存在するか確認
    var checkboxes = getWarrantyCheckboxes();

    if (checkboxes.length === 0) {
      // Globo がまだ DOM を構築していない場合、少し待ってリトライ
      log('保証チェックボックスが未検出、500ms後にリトライ...');
      setTimeout(init, 500);
      return;
    }

    // 初回: ベース金額を記録してから計算
    updateBaseTotal();
    updateWarrantyDisplays();

    // 監視開始
    observePriceChanges();
    observeCheckboxChanges();

    log('初期化完了');
  }

  // ============================================================
  // エントリーポイント
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      // Globo の初期化を待つために少し遅延
      setTimeout(init, 800);
    });
  } else {
    setTimeout(init, 800);
  }

})();